/**
 * Properties Modal Component
 * Fullscreen modal for editing control properties
 * Phase 4: Enhanced with live preview split view
 * Phase 5: Refactored to use shared Modal component
 */

import React, { useState } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { FormField, FormThemeProvider } from '@atlas-forms/player-components-react';
import { useTheme } from '../../hooks/useTheme';
import { Modal } from '../shared/Modal';
import { Button } from '../shared/Button';
import { PropertiesTab, PropertiesTabProps } from './PropertiesTab';
import '../../styles/tabs/PropertiesModal.css';
import { EyeIcon, EyeOffIcon } from '@atlas-forms/ui-components-react';

export interface PropertiesModalProps extends PropertiesTabProps {
  /**
   * Whether modal is open
   */
  isOpen: boolean;

  /**
   * Callback when closing modal
   */
  onClose: () => void;
}

/**
 * Properties Modal - Fullscreen properties editor with live preview
 * Phase 4: Split view with real-time control preview
 */
export const PropertiesModal: React.FC<PropertiesModalProps> = ({
  control,
  onUpdateControl,
  isOpen,
  onClose,
}) => {
  const { theme } = useTheme();
  const [showPreview, setShowPreview] = useState(true);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="fullscreen"
      className="af-properties-modal"
      contentClassName="af-properties-modal__modal-content"
      ariaLabel={`Edit Properties - ${control?.label || control?.id || 'Control'}`}
      title={
        <div className="af-properties-modal__header-content">
          <span>
            Edit Properties
            {control && ` - ${control.label || control.id}`}
          </span>
          <Button
            variant="ghost"
            size="small"
            onClick={() => setShowPreview(!showPreview)}
            aria-label={showPreview ? "Hide preview" : "Show preview"}
            title={showPreview ? "Hide preview" : "Show preview"}
          >
            {showPreview ? (
              <><EyeOffIcon width={16} height={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} /> Hide Preview</>
            ) : (
              <><EyeIcon width={16} height={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} /> Show Preview</>
            )}
          </Button>
        </div>
      }
    >
      <div className="af-properties-modal__body">
        {showPreview && control ? (
          <PanelGroup direction="horizontal" style={{ height: '100%' }}>
            {/* Left - Properties Editor */}
            <Panel defaultSize={60} minSize={40}>
              <div className="af-properties-modal__properties-panel">
                <PropertiesTab
                  control={control}
                  onUpdateControl={onUpdateControl}
                  hideFullscreen
                />
              </div>
            </Panel>

            <PanelResizeHandle className="af-panel-resize-handle" />

            {/* Right - Live Preview */}
            <Panel defaultSize={40} minSize={30}>
              <div className="af-properties-modal__preview-panel">
                <div className="af-properties-modal__preview-header">
                  <h3>Live Preview</h3>
                  <span className="af-properties-modal__preview-badge">Updates in real-time</span>
                </div>
                <div className="af-properties-modal__preview-content">
                  <FormThemeProvider theme={theme}>
                    <FormField
                      control={control}
                      value={control.defaultValue || undefined}
                      mode="edit"
                      onChange={() => {}}
                      onBlur={() => {}}
                    />
                  </FormThemeProvider>
                </div>
              </div>
            </Panel>
          </PanelGroup>
        ) : (
          <PropertiesTab
            control={control}
            onUpdateControl={onUpdateControl}
            hideFullscreen
          />
        )}
      </div>
    </Modal>
  );
};

export default PropertiesModal;
